// Push in the native shell (X13-1009b, B251; wired from the web app by X13-1009c). The shell loads the web app, so this file is
// the pure logic only, shared by the web boot (pwa/native-push.ts) and apps/mobile's tests: pushPlan turns a plugin event into
// what the shell does next (send this device's token to the server, or open a portal link). The server side is
// POST /api/v1/push/native (X13-1009a), which stores the token on the signed-in person's row. A tapped notification opens its
// link only when that link is on the portal of this shell; anything else is ignored. No import here: it runs in node and browser.

export type Platform = "ios" | "android";

/** The registration the plugin reports: `value` is the device's FCM registration token, the platform comes from Capacitor. */
export type RegistrationEvent = { platform: Platform; value: string };

/** A tapped notification as the plugin reports it. The server sends `url` (the full link) and `tag` as the data. */
export type TapEvent = { actionId: string; notification: { data?: { url?: unknown; tag?: unknown } } };

export type PushPlan = {
  send?: { path: "/push/native"; body: { platform: Platform; token: string } };
  open?: string;
};

/**
 * What the shell does for one plugin event. A registration asks the server to store the token. A tap opens its link only when
 * the link is an address under /portal/ on this shell's own origin (origin is the portal address the shell loads); a foreign
 * address, a missing one, or one that is not a portal page gives no open.
 */
export function pushPlan(event: RegistrationEvent | TapEvent, origin: string): PushPlan {
  if ("value" in event) {
    if (!event.value.trim()) return {};
    return { send: { path: "/push/native", body: { platform: event.platform, token: event.value } } };
  }
  const href = event.notification?.data?.url;
  if (typeof href !== "string" || !href) return {};
  let link: URL;
  try {
    link = new URL(href);
  } catch {
    return {};
  }
  if (link.origin !== new URL(origin).origin || !link.pathname.startsWith("/portal/")) return {};
  return { open: `${link.pathname}${link.search}${link.hash}` };
}

/** The part of the push plugin the shell uses. The plugin object satisfies it; tests pass a recorder. */
export type PushPlugin = {
  addListener(event: "registration", fn: (value: { value: string }) => void): unknown;
  addListener(event: "pushNotificationActionPerformed", fn: (action: TapEvent) => void): unknown;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
};

/**
 * Asks for permission, registers the device, and maps the plugin's events through pushPlan. `post` sends the server request
 * (the portal's own origin, signed in); `openLink` navigates the shell to a portal path. Returns false when the person did
 * not allow notifications, in which case nothing is registered.
 */
export async function wirePush(
  plugin: PushPlugin,
  platform: Platform,
  origin: string,
  post: (path: "/push/native", body: { platform: Platform; token: string }) => Promise<unknown>,
  openLink: (path: string) => void,
): Promise<boolean> {
  plugin.addListener("registration", (value) => {
    const plan = pushPlan({ platform, value: value.value }, origin);
    // a failed post is not retried here: the plugin registers again on every launch, so the token is sent then
    if (plan.send) post(plan.send.path, plan.send.body).catch(() => undefined);
  });
  plugin.addListener("pushNotificationActionPerformed", (action) => {
    const plan = pushPlan(action, origin);
    if (plan.open) openLink(plan.open);
  });
  const permission = await plugin.requestPermissions();
  if (permission.receive !== "granted") return false;
  await plugin.register();
  return true;
}
